import { createRoot } from "react-dom/client";
import { ThemeProvider } from "next-themes"
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from "@react-oauth/google";
import App from "./App";
import { store } from './store';
import { injectStore } from "./lib/api-client";
import { initCrossTabLogoutListener, initCrossTabLoginListener } from "./lib/session-sync";
import { logout } from "./modules/auth/store/authSlice";
import { clearAllHubConnections } from "./modules/recruitment/applications/infrastructure/signalr-service";
import { SessionInitializer } from "./modules/auth/shared/components/SessionInitializer";
import { Toaster } from "@/modules/shared/ui/components/sonner";
import { setupMocks } from "./mocks";
import "./App.css"
import "./lib/analytics";

injectStore(store);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const fullSessionClear = () => {
  queryClient.clear();
  clearAllHubConnections();
  sessionStorage.clear();
  store.dispatch(logout());
};

initCrossTabLogoutListener(fullSessionClear);

initCrossTabLoginListener(
  () => store.getState().auth.user?.user?.email ?? null,
  fullSessionClear,
);

const prepare = async () => {
  const env = import.meta.env.DEV;
  const useApiMocks = import.meta.env.VITE_USE_API_MOCKS;

  if (env && useApiMocks === "true") {
    setupMocks()
  }
}

const root = createRoot(document.getElementById("root")!);

prepare().then(() => {
  root.render(
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <SessionInitializer>
              <App />
              <Toaster
                position="top-right"
                className="toaster group"
                expand={true}
                toastOptions={{
                  classNames: {
                    toast: "!bg-primary !text-primary-foreground group-data-[state=open]:!bg-primary"
                  }
                }}
              />
            </SessionInitializer>
          </Provider>
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
});
